import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fC = (v) => {
  const n = Number(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const TDB_CITIES = ['Bengaluru', 'Mumbai', 'Gurgaon', 'Online / B2B'];

/**
 * TDB Cost — "By City" tab. Pick a city → see cost breakdown by category for
 * the selected month + 6-month trend bar chart. Also shows cost-as-%-of-rev
 * so Aditya can spot margin erosion early.
 */
export default function TDBCostByCityTab() {
  const todayYm = new Date().toISOString().slice(0, 7);
  const [monthYm, setMonthYm] = useState(todayYm);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [activeCity, setActiveCity] = useState('Bengaluru');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r1, r2] = await Promise.all([
          api.get(`/tdb/cost-entries/by-city?month_ym=${monthYm}`),
          api.get(`/tdb/cost-entries/trend?city=${encodeURIComponent(activeCity)}&months=6`),
        ]);
        setData(r1.data);
        setTrend(r2.data);
      } catch (e) {
        toast.error('Failed to load city breakdown');
      } finally {
        setLoading(false);
      }
    })();
  }, [monthYm, activeCity]);

  const activeData = (data?.cities || {})[activeCity] || { total: 0, by_category: {}, revenue: 0, cost_as_pct_of_revenue: 0 };
  const maxTrend = Math.max(...((trend?.months || []).map(m => m.total)), 1);
  const sortedCats = useMemo(() => {
    return Object.entries(activeData.by_category || {}).sort(([, a], [, b]) => b - a);
  }, [activeData.by_category]);

  return (
    <div className="space-y-5" data-testid="tdb-cost-by-city-tab">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wide block mb-0.5" style={{ color: '#9CA3AF' }}>Month</label>
          <input type="month" value={monthYm} onChange={(e) => setMonthYm(e.target.value)}
            className="rounded border px-2 py-1.5 text-sm" style={{ borderColor: '#E5E1DC' }}
            data-testid="tdb-bycity-month" />
        </div>
      </div>

      {/* City chips */}
      <div className="flex flex-wrap gap-2">
        {TDB_CITIES.map((c) => {
          const rev = (data?.cities || {})[c]?.revenue || 0;
          const cost = (data?.cities || {})[c]?.total || 0;
          const isActive = activeCity === c;
          return (
            <button key={c} onClick={() => setActiveCity(c)}
              className="text-left px-4 py-3 rounded-lg border min-w-[160px]"
              style={{
                borderColor: isActive ? '#2A7B88' : '#E5E1DC',
                backgroundColor: isActive ? '#F0FDF4' : 'white',
                borderWidth: isActive ? 2 : 1,
              }}
              data-testid={`tdb-bycity-chip-${c.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`}>
              <div className="text-[11px] font-medium" style={{ color: '#6B7280' }}>{c}</div>
              <div className="text-sm font-semibold mt-0.5 font-mono" style={{ color: isActive ? '#065F46' : '#1B2A4A' }}>
                Cost {fC(cost)}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>Rev {fC(rev)}</div>
            </button>
          );
        })}
      </div>

      {loading && <p className="text-xs py-8 text-center" style={{ color: '#9CA3AF' }}>Loading…</p>}

      {!loading && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Category breakdown card */}
          <div className="rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
                {activeCity} · {monthYm}
              </h3>
              <span className="text-[11px]" style={{ color: '#9CA3AF' }}>
                Cost as % of revenue:
                <strong className="ml-1" style={{ color: '#1B2A4A' }}>
                  {activeData.cost_as_pct_of_revenue != null
                    ? `${activeData.cost_as_pct_of_revenue.toFixed(1)}%`
                    : '—'}
                </strong>
              </span>
            </div>
            <p className="text-xl font-bold mb-3 font-mono" style={{ color: '#1B2A4A' }}>{fC(activeData.total)}</p>
            {sortedCats.length === 0 ? (
              <p className="text-xs py-6 text-center" style={{ color: '#9CA3AF' }}>No costs logged for {activeCity} in {monthYm}.</p>
            ) : (
              <div className="space-y-2">
                {sortedCats.map(([cat, amt]) => {
                  const pct = activeData.total > 0 ? (amt / activeData.total * 100) : 0;
                  return (
                    <div key={cat}>
                      <div className="flex justify-between text-[11.5px] mb-0.5">
                        <span style={{ color: '#6B7280' }}>{cat}</span>
                        <span className="font-mono" style={{ color: '#1B2A4A' }}>{fC(amt)} <span style={{ color: '#9CA3AF' }}>· {pct.toFixed(0)}%</span></span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ backgroundColor: '#F3F0EB' }}>
                        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: '#2A7B88' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Trend chart card */}
          <div className="rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }}>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>
              {activeCity} · 6-month cost trend
            </h3>
            <div className="flex items-end justify-between gap-2 h-36" data-testid="tdb-bycity-trend">
              {(trend?.months || []).map((m) => (
                <div key={m.month_ym} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-t relative group"
                    style={{ height: `${(m.total / maxTrend) * 100}%`, backgroundColor: '#2A7B88', minHeight: m.total ? 4 : 0 }}
                    title={`${m.month_ym}: ${fC(m.total)} (${m.count} entries)`}>
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-mono whitespace-nowrap opacity-0 group-hover:opacity-100"
                      style={{ color: '#1B2A4A' }}>
                      {fC(m.total)}
                    </span>
                  </div>
                  <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{m.month_ym.slice(5)}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] mt-3" style={{ color: '#9CA3AF' }}>
              Hover a bar for the month's total. Bars drawn from <code>cost-entries</code> grouped by month.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
